import axios from '@/plugins/axios';
import { CACHE_TIME } from '@/js/utils/vars';

export const namespaced = true;

export const state = () => ({
    entities: [],
    home_page_entities: [],
    entitiesCount: 0,
    entity: {},
    categories: [],
    products: [],
    productsCount: 0,
    popular_list: [],
    recomendations: [],
    profile_recomendations: [],
    new_partners: [],
    product: {},
    settings: {},
    lastUpdate: 0,
    listFilters: {
        min_price_max: null,
        delivery_time_min: null,
        delivery_time_max: null,
        cuisine_type_ids: [],
        restaurant_type_ids: [],
        sort: 'default'
    },
    // Cache timestamps for 5-minute caching
    cacheTimestamps: {
        fetch: 0,
        homePage: 0,
        fetchByTower: {},
        popular: {},
        recomendations: {},
        profileRecomendations: 0,
        newPartners: 0
    },
    skeletonList: [
        {
            id: 1,
            skeleton: true,
        }, 
        {
            id: 2,
            skeleton: true,
        }, 
        {
            id: 3,
            skeleton: true,
        }, 
        {
            id: 4,
            skeleton: true,
        }, 
        {
            id: 5,
            skeleton: true,
        }, 
        {
            id: 6,
            skeleton: true,
        }, 
        {
            id: 7,
            skeleton: true,
        }, 
        {
            id: 8,
            skeleton: true,
        }, 
    ]
});

export const mutations = {
    CLEAR_ENTITIES(state) {
        state.entities = [];
        state.entitiesCount = 0;
    },
    CLEAR_ENTITY(state) {
        state.entity = {};
        state.categories = [];
    },
    CLEAR_CATEGORIES(state) {
        state.categories = [];
    },
    CLEAR_PRODUCTS(state) {
        state.products = [];
        state.productsCount = 0;
    },
    CLEAR_PRODUCT(state) {
        state.product = {};
    },
    CLEAR_RESTAURANT_SETTINGS(state) {
        state.settings = {};
    },
    SET_ENTITIES(state, payload) {
        state.entities = payload;
    },
    SET_HOME_PAGE_ENTITIES(state, payload) {
        state.home_page_entities = payload;
    },
    SET_ENTITY(state, payload) {
        state.entity = payload;
    },
    SET_CATEGORIES(state, payload) {
        state.categories = payload;
    },
    SET_PRODUCTS(state, payload) {
        if(payload.page === 1) {
            state.products = [];
        }
        state.products = state.products.concat(payload.products);
        state.productsCount = payload.productsCount;
    },
    RESET_PRODUCTS(state) {
        state.products = []
    },
    SET_POPULAR(state, payload) {
        state.popular_list = payload;
    },
    SET_RECOMENDATIONS(state, payload) {
        state.recomendations = payload;
    },
    SET_PROFILE_RECOMENDATIONS(state, payload) {
        state.profile_recomendations = payload;
    },
    SET_NEW_PARTNERS(state, payload) {
        state.new_partners = payload;
    },
    SET_PRODUCT(state, payload) {
        state.product = payload;
    },
    SET_RESTAURANT_SETTINGS(state, payload) {
        state.settings = payload;
    },
    SET_LIST_FILTERS(state, payload) {
        state.listFilters = {
            ...state.listFilters,
            ...payload
        };
    },
    UPDATE_CACHE_TIMESTAMP(state, { method, key = null, timestamp }) {
        if (key) {
            state.cacheTimestamps[method][key] = timestamp;
        } else {
            state.cacheTimestamps[method] = timestamp;
        }
    }
};

export const actions = {
    async fetch({ commit, state }, { forced = false, filters = {}, sort = null } = {}) {
        const now = (new Date()).getTime();
        const hasFilters = !!(
            (filters && (
                filters.min_price_max !== null && filters.min_price_max !== undefined ||
                filters.delivery_time_min !== null && filters.delivery_time_min !== undefined ||
                filters.delivery_time_max !== null && filters.delivery_time_max !== undefined ||
                (filters.cuisine_type_ids && filters.cuisine_type_ids.length) ||
                (filters.restaurant_type_ids && filters.restaurant_type_ids.length)
            ))
        );
        const hasParams = !!(sort || hasFilters);
        const cacheValid = !forced && !hasParams && state.entities.length && (now - state.cacheTimestamps.fetch < CACHE_TIME);
        
        if (cacheValid) {
            return state.entities;
        }
        
        const params = new URLSearchParams();
        if (sort) params.append('sort', sort);
        if (filters?.min_price_max !== null && typeof filters?.min_price_max !== 'undefined') params.append('min_price_max', filters.min_price_max);
        if (filters?.delivery_time_min !== null && filters?.delivery_time_min !== undefined) params.append('delivery_time_min', filters.delivery_time_min);
        if (filters?.delivery_time_max !== null && filters?.delivery_time_max !== undefined) params.append('delivery_time_max', filters.delivery_time_max);
        if (filters?.cuisine_type_ids?.length) params.append('cuisine_type_ids', filters.cuisine_type_ids.join(','));
        if (filters?.restaurant_type_ids?.length) params.append('restaurant_type_ids', filters.restaurant_type_ids.join(','));
        const query = params.toString() ? `?${params.toString()}` : '';

        const { data } = await axios.get(`/front/restaurants${query}`);
        console.log('[FRONT:STORE] Получены данные от API, количество ресторанов:', data?.length || 0);
        if (data && data.length) {
            const restaurant72 = data.find(r => r.id === 72);
            console.log('[FRONT:STORE] Ресторан 72 в ответе API:', restaurant72 ? 'ДА' : 'НЕТ', restaurant72 ? `(closed: ${restaurant72.closed}, hide: ${restaurant72.hide})` : '');
        }
        commit('SET_ENTITIES', data);
        commit('SET_LIST_FILTERS', {
            min_price_max: typeof filters?.min_price_max !== 'undefined' ? filters.min_price_max : null,
            delivery_time_min: typeof filters?.delivery_time_min !== 'undefined' && filters?.delivery_time_min !== null ? filters.delivery_time_min : null,
            delivery_time_max: typeof filters?.delivery_time_max !== 'undefined' && filters?.delivery_time_max !== null ? filters.delivery_time_max : null,
            cuisine_type_ids: filters?.cuisine_type_ids || [],
            restaurant_type_ids: filters?.restaurant_type_ids || [],
            sort: sort || 'default'
        });
        if (!hasParams) {
            commit('UPDATE_CACHE_TIMESTAMP', { method: 'fetch', timestamp: now });
        }
        return data;
    },
    async homePage({ commit, state }, { forced = false } = {}) {
        const now = (new Date()).getTime();
        const cacheValid = !forced && state.home_page_entities.length && (now - state.cacheTimestamps.homePage < CACHE_TIME);
        
        if (cacheValid) {
            return state.home_page_entities;
        }
        
        const { data } = await axios.get('/front/restaurants/main_page');
        commit('SET_HOME_PAGE_ENTITIES', data);
        commit('UPDATE_CACHE_TIMESTAMP', { method: 'homePage', timestamp: now });
        return data;
    },
    async fetchByTower({ commit, state }, { tower_id, forced = false, filters = {}, sort = null }) {
        const now = (new Date()).getTime();
        const cacheKey = tower_id.toString();
        const hasFilters = !!(
            (filters && (
                filters.min_price_max !== null && filters.min_price_max !== undefined ||
                filters.delivery_time_min !== null && filters.delivery_time_min !== undefined ||
                filters.delivery_time_max !== null && filters.delivery_time_max !== undefined ||
                (filters.cuisine_type_ids && filters.cuisine_type_ids.length) ||
                (filters.restaurant_type_ids && filters.restaurant_type_ids.length)
            ))
        );
        const hasParams = !!(sort || hasFilters);
        const cacheValid = !forced && !hasParams && state.entities.length && 
                          state.cacheTimestamps.fetchByTower[cacheKey] &&
                          (now - state.cacheTimestamps.fetchByTower[cacheKey] < CACHE_TIME);
        
        if (cacheValid) {
            return state.entities;
        }
        
        const params = new URLSearchParams();
        if (sort) params.append('sort', sort);
        if (filters?.min_price_max !== null && typeof filters?.min_price_max !== 'undefined') params.append('min_price_max', filters.min_price_max);
        if (filters?.delivery_time_min !== null && filters?.delivery_time_min !== undefined) params.append('delivery_time_min', filters.delivery_time_min);
        if (filters?.delivery_time_max !== null && filters?.delivery_time_max !== undefined) params.append('delivery_time_max', filters.delivery_time_max);
        if (filters?.cuisine_type_ids?.length) params.append('cuisine_type_ids', filters.cuisine_type_ids.join(','));
        if (filters?.restaurant_type_ids?.length) params.append('restaurant_type_ids', filters.restaurant_type_ids.join(','));
        const query = params.toString() ? `?${params.toString()}` : '';

        const { data } = await axios.get(`/front/restaurants/sort-for-tower/${tower_id}${query}`);
        commit('SET_ENTITIES', data);
        commit('SET_LIST_FILTERS', {
            min_price_max: typeof filters?.min_price_max !== 'undefined' ? filters.min_price_max : null,
            delivery_time_min: typeof filters?.delivery_time_min !== 'undefined' && filters?.delivery_time_min !== null ? filters.delivery_time_min : null,
            delivery_time_max: typeof filters?.delivery_time_max !== 'undefined' && filters?.delivery_time_max !== null ? filters.delivery_time_max : null,
            cuisine_type_ids: filters?.cuisine_type_ids || [],
            restaurant_type_ids: filters?.restaurant_type_ids || [],
            sort: sort || 'default'
        });
        if (!hasParams) {
            commit('UPDATE_CACHE_TIMESTAMP', { method: 'fetchByTower', key: cacheKey, timestamp: now });
        }
        return data;
    },
    async get({ commit }, { restaurantId }) {
        const { data } = await axios.get(`/front/restaurants/${ restaurantId }`);
        commit('SET_CATEGORIES', data.categories);
        delete data.categories;
        commit('SET_ENTITY', data);
    },
    async getByCode({ commit }, { restaurantCode }) {
        const { data } = await axios.get(`/front/restaurants/code/${ restaurantCode }`);
        commit('SET_CATEGORIES', data.categories);
        delete data.categories;
        commit('SET_ENTITY', data);
        return data;
    },
    async getProducts({ commit }, { restaurantId, categoryId, page }) {
        if(page === 1) {
            commit('SET_PRODUCTS', { products: [], productsCount: 0, page: 1 });
        }
        const { data } = await axios.get(`/front/restaurants/${ restaurantId }/category/${ categoryId }/dishes?page=${ page }`);
        commit('SET_PRODUCTS', { products: data.results, productsCount: data.total, page });
    },
    async getProductsPage(_, { restaurantId, categoryId, page }) {
        const { data } = await axios.get(`/front/restaurants/${ restaurantId }/category/${ categoryId }/dishes?page=${ page }`);
        return data;
    },
    async getPopular({ commit, state }, { restaurantId, forced = false }) {
        const now = (new Date()).getTime();
        const cacheKey = restaurantId.toString();
        const cacheValid = !forced && state.popular_list.length && 
                          state.cacheTimestamps.popular[cacheKey] &&
                          (now - state.cacheTimestamps.popular[cacheKey] < CACHE_TIME);
        
        if (cacheValid) {
            return state.popular_list;
        }
        
        const { data } = await axios.get(`/front/restaurants/${ restaurantId }/popular`);
        commit('SET_POPULAR', data);
        commit('UPDATE_CACHE_TIMESTAMP', { method: 'popular', key: cacheKey, timestamp: now });
        return data;
    },
    async getRecomendations({ commit, state }, { restaurantId, forced = false }) {
        const { data } = await axios.get(`/front/restaurants/${ restaurantId }/recomendations`);
        commit('SET_RECOMENDATIONS', data);
        return data;
    },
    async getProfileRecomendations({ commit, state }, { forced = false } = {}) {
        const now = (new Date()).getTime();
        const cacheValid = !forced && state.profile_recomendations.length && (now - state.cacheTimestamps.profileRecomendations < CACHE_TIME);
        
        if (cacheValid) {
            return state.profile_recomendations;
        }
        
        const { data } = await axios.get('/front/restaurants/popular/profile');
        commit('SET_PROFILE_RECOMENDATIONS', data);
        commit('UPDATE_CACHE_TIMESTAMP', { method: 'profileRecomendations', timestamp: now });
        return data;
    },
    async getNewPartners({ commit, state }, { forced = false } = {}) {
        const now = (new Date()).getTime();
        const cacheValid = !forced && state.new_partners.length && (now - state.cacheTimestamps.newPartners < CACHE_TIME);
        
        if (cacheValid) {
            return state.new_partners;
        }
        
        const { data } = await axios.get('/front/restaurants/new-partners');
        commit('SET_NEW_PARTNERS', data);
        commit('UPDATE_CACHE_TIMESTAMP', { method: 'newPartners', timestamp: now });
        return data;
    },
    async getProduct({ commit }, { restaurantId, productId }) {
        commit('SET_PRODUCT', {});
        const { data } = await axios.get(`/front/restaurants/${ restaurantId }/dishes/${ productId }`);
        commit('SET_PRODUCT', data);
    },
    async fetchRestaurantSettings({ commit }, { restaurantId }) {
        const { data } = await axios.get(`/front/restaurants/settings/${restaurantId}`);
        commit('SET_RESTAURANT_SETTINGS', data);
    }
};

export const getters = {
    categoriesChildrenMap: ({ categories }) => {
        const res = { 0: [] };

        categories.forEach(category => {
            if (!category.parent_id) {
                res[0].push(category);
                return;
            }

            if (res[category.parent_id]) res[category.parent_id].push(category);
            else res[category.parent_id] = [category];
        });

        return res;
    },
    
    categoriesByIdMap: ({ categories }) => {
        const res = {};

        categories.forEach(category => {

            res[category.id ?? 0] = category;

            return res;
        });

        return res;
    },

    addedSubcategoriesCategoriesByIdMap: (state, { categoriesByIdMap, categoriesChildrenMap }) => {
        const res = {
            0: { id: 'root', subcategories: categoriesChildrenMap[0] }
        };

        Object.keys(categoriesByIdMap).forEach(id => {
            const newCategory = { ...categoriesByIdMap[id], subcategories: categoriesChildrenMap[id] ?? [] };

            if (!res[id]) res[id] = newCategory;
        });

        return res;
    },

    enabledEntities: ({ entities }) => entities.filter(entity => !entity.hide)
};
